/**
 * main.ts — orchestration: model load, rAF render loop, agent tick timer,
 * painting, lifecycle, head-to-head comparator.
 *
 * Lifecycle:
 *   - idle     — model loaded; user paints a layout.
 *   - running  — agent ticks every (1000/Hz) ms; episode ends when env.done,
 *                at which point we restart with a FRESH LAYOUT (the agent must
 *                generalise, not memorise).
 *   - stopped  — timer cleared; last frame stays up.
 *
 * Comparator:
 *   "Run 30 episodes" button takes the CURRENT world layout (snapshot at
 *   button-press time), runs 30 trained + 30 random episodes on the SAME 30
 *   layouts, and displays the ratio. This is the live demo's headline number.
 */

import './style.css';
import {
  World, Env, makeClusteredWorld,
  GRID, MAX_STEPS, FREE, GOLD, STONE, V_MAX, K_MAX,
} from './environment';
import {
  LitertAgent, runRandomEpisode, runGreedyEpisode,
  type Agent, type LoopGateDiagnosticsForHud,
} from './agent';
import { LitertMemAgent } from './agent_mem';
import { Renderer, makeAgentAnim, type AgentAnim } from './render';
import { SidePanel, type Brush, type PanelUpdate } from './ui';
import { HelpModal } from './help';

const START_R = GRID >> 1;
const START_C = GRID >> 1;
const DEFAULT_SPEED_HZ = 15;
const N_COMPARE_EPISODES = 30;

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

async function main(): Promise<void> {
  const app = document.getElementById('app')!;
  app.innerHTML = '';

  // Layout: canvas (left) + side panel (right).
  const canvas = document.createElement('canvas');
  canvas.className = 'lake';
  const canvasWrap = document.createElement('div');
  canvasWrap.className = 'lake-wrap';
  canvasWrap.appendChild(canvas);
  app.appendChild(canvasWrap);

  const panelSlot = document.createElement('div');
  panelSlot.className = 'panel-slot';
  app.appendChild(panelSlot);

  // ── Status banner (shown while the model is loading) ──────────────────
  const status = document.createElement('div');
  status.className = 'status-banner';
  status.textContent = 'Loading gru.tflite + gru_mem.tflite via LiteRT.js…';
  app.appendChild(status);

  // ── Core objects ──────────────────────────────────────────────────────
  const world = new World(GRID);
  const initial = makeClusteredWorld(GRID, 42);
  world.loadFrom(initial.cells, initial.values);

  const env = new Env(world, mulberry32(99));
  env.snapshotWorld();
  const renderer = new Renderer(canvas);

  // Both agents are pre-loaded at startup; the sidebar selector just swaps
  // which one `activeAgent` points at (and re-starts the episode so the
  // recurrence resets cleanly). Each agent owns its own LoopGate; the gate
  // toggles below apply to whichever agent is currently active.
  const baseAgent = new LitertAgent({
    modelUrl: './gru.tflite',
    wasmPath: './litert-wasm/',
    accelerator: 'wasm',
  });
  const memAgent = new LitertMemAgent({
    modelUrl: './gru_mem.tflite',
    wasmPath: './litert-wasm/',
    accelerator: 'wasm',
  });
  let activeAgent: Agent = baseAgent;
  let activeVariant: 'base' | 'mem' = 'base';

  let agentAnim: AgentAnim = makeAgentAnim(START_R, START_C);

  // Mutable UI state.
  let brush: Brush = 'gold';
  let speedHz = DEFAULT_SPEED_HZ;
  let revealAll = false;
  let showBelief = true;
  let showSensor = true;

  // Run state.
  type Phase = 'idle' | 'running' | 'stopped';
  let phase: Phase = 'idle';
  let tickTimerId: number | null = null;
  let stepping = false;       // guards against re-entrant ticks (async step)
  let episodes = 0;
  let totalCollected = 0;
  let lastQ: ArrayLike<number> = new Float64Array(8);
  let lastHNorm = 0;
  let lastRatio: number | null = null;
  let comparing = false;
  let lastGate: LoopGateDiagnosticsForHud | null = null;
  let gateEnabled = true;   // matches AgentOptions.loopGate default
  let tabooEnabled = false; // matches AgentOptions.taboo default (variance reducer, off)

  // ── Side panel ────────────────────────────────────────────────────────
  const panel = new SidePanel({
    onBrush: (b) => { brush = b; },
    onClear: () => {
      if (phase === 'running') return;
      world.loadFrom(new Uint8Array(GRID * GRID), new Float64Array(GRID * GRID));
      env.snapshotWorld();
      env.reset();
      pushUpdate();
    },
    onGenerate: () => {
      if (phase === 'running') return;
      const g = makeClusteredWorld(GRID, (Math.random() * 1e9) | 0);
      world.loadFrom(g.cells, g.values);
      env.snapshotWorld();
      env.reset();
      pushUpdate();
    },
    onSpeed: (hz) => { speedHz = hz; if (phase === 'running') restartTickTimer(); },
    onReveal: (b) => { revealAll = b; },
    onShowBelief: (b) => { showBelief = b; },
    onShowSensor: (b) => { showSensor = b; },
    onStart: () => startRun(),
    onStop: () => stopRun(),
    onReset: () => resetRun(),
    onCompare: async () => { await runComparison(); },
    onToggleGate: (enabled) => {
      // Runtime toggle. The gate's internal state keeps updating
      // regardless, so toggling back on mid-episode resumes smoothly.
      activeAgent.gateEnabled = enabled;
      gateEnabled = enabled;
    },
    onToggleTaboo: (enabled) => {
      // Runtime toggle for the basin-cell taboo mask (second mask).
      // Only meaningful when the gate is enabled; the UI disables the
      // taboo checkbox when the gate is off, but we also guard here.
      if (!gateEnabled) return;
      activeAgent.tabooEnabled = enabled;
      tabooEnabled = enabled;
    },
    onSelectModel: (variant) => {
      // Swap the active agent. Stop any in-flight run, re-start the episode
      // so the recurrence + gate reset cleanly on the new model. The user
      // can press Start again to resume.
      if (variant === activeVariant) return;
      stopRun();
      activeVariant = variant;
      activeAgent = variant === 'mem' ? memAgent : baseAgent;
      // Sync the gate/taboo toggles to whatever the new agent has (they
      // may have been toggled independently on the previous model).
      activeAgent.gateEnabled = gateEnabled;
      activeAgent.tabooEnabled = tabooEnabled;
      env.reset();
      activeAgent.startEpisode(env);
      agentAnim = makeAgentAnim(START_R, START_C);
      pushUpdate();
    },
  });
  panelSlot.appendChild(panel.el);

  // ── URL param: ?model={base|mem} ──────────────────────────────────────
  // Lets links (e.g. from a blog post) pre-select the active model. Validated
  // against the two known variants; an unknown or absent value leaves the
  // default 'base'. Parsed before agent.load() so the right model is active
  // by the time the first episode runs. The selector click is also pushed
  // through the panel callback so the UI highlight stays in sync.
  const urlModel = new URLSearchParams(window.location.search).get('model');
  if (urlModel === 'base' || urlModel === 'mem') {
    if (urlModel !== activeVariant) {
      activeVariant = urlModel;
      activeAgent = urlModel === 'mem' ? memAgent : baseAgent;
      // Sync the gate/taboo toggles to the new agent's current state.
      activeAgent.gateEnabled = gateEnabled;
      activeAgent.tabooEnabled = tabooEnabled;
      env.reset();
      activeAgent.startEpisode(env);
      agentAnim = makeAgentAnim(START_R, START_C);
    }
  }

  // ── Help button + modal ───────────────────────────────────────────────
  // Fixed top-left, mounted to document.body so it floats above every
  // overlay (mirrors vacuum-demo's pattern).
  const helpBtn = document.createElement('button');
  helpBtn.className = 'help-btn';
  helpBtn.type = 'button';
  helpBtn.textContent = '?';
  helpBtn.setAttribute('aria-label', 'What is this?');
  document.body.appendChild(helpBtn);
  let helpModal: HelpModal | null = null;
  function showHelp(): void {
    if (helpModal) return;
    helpModal = new HelpModal(() => {
      helpModal?.remove();
      helpModal = null;
    });
    helpModal.mount(document.body);
  }
  helpBtn.addEventListener('click', showHelp);

  // ── Painting ──────────────────────────────────────────────────────────
  let painting = false;
  let lastPaintCell: { r: number; c: number } | null = null;

  function eventToCell(e: PointerEvent | MouseEvent): { r: number; c: number } | null {
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const w = rect.width;
    const h = rect.height;
    const cell = Math.min(w / GRID, h / GRID);
    const ox = (w - cell * GRID) / 2;
    const oy = (h - cell * GRID) / 2;
    const c = Math.floor((x - ox) / cell);
    const r = Math.floor((y - oy) / cell);
    if (r < 0 || r >= GRID || c < 0 || c >= GRID) return null;
    return { r, c };
  }

  function paintAt(r: number, c: number): void {
    // Don't paint over the agent's start cell.
    if (r === START_R && c === START_C) return;
    const kind = brush === 'gold' ? GOLD : brush === 'stone' ? STONE : FREE;
    // Graded values: gold gets a Beta(2,5)-ish value, stone a small cost.
    // Use the same RNG so consecutive paints vary.
    const v = kind === GOLD
      ? 0.3 + Math.random() * 0.7 * V_MAX
      : kind === STONE
      ? 0.05 + Math.random() * 0.15 * K_MAX
      : 0;
    world.paint(r, c, kind, v);
    env.snapshotWorld();
    env.resyncValueLeft();
  }

  function paintLine(a: { r: number; c: number }, b: { r: number; c: number }): void {
    let dx = Math.abs(b.c - a.c);
    let dy = -Math.abs(b.r - a.r);
    const sx = a.c < b.c ? 1 : -1;
    const sy = a.r < b.r ? 1 : -1;
    let err = dx + dy;
    let cr = a.r;
    let cc = a.c;
    while (true) {
      paintAt(cr, cc);
      if (cr === b.r && cc === b.c) break;
      const e2 = 2 * err;
      if (e2 >= dy) { err += dy; cc += sx; }
      if (e2 <= dx) { err += dx; cr += sy; }
    }
  }

  canvas.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    canvas.setPointerCapture(e.pointerId);
    painting = true;
    const cell = eventToCell(e);
    if (cell) { paintAt(cell.r, cell.c); lastPaintCell = cell; }
  });
  canvas.addEventListener('pointermove', (e) => {
    if (!painting) return;
    const cell = eventToCell(e);
    if (cell) {
      if (lastPaintCell) paintLine(lastPaintCell, cell);
      else paintAt(cell.r, cell.c);
      lastPaintCell = cell;
    }
  });
  const endPaint = (e: PointerEvent) => {
    painting = false;
    lastPaintCell = null;
    try { canvas.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  canvas.addEventListener('pointerup', endPaint);
  canvas.addEventListener('pointercancel', endPaint);
  canvas.addEventListener('pointerleave', () => { lastPaintCell = null; });

  // ── Render loop ───────────────────────────────────────────────────────
  const ro = new ResizeObserver(() => renderer.resize());
  ro.observe(canvas);

  requestAnimationFrame(function frame() {
    agentAnim.r = env.r;
    agentAnim.c = env.c;
    renderer.frame(performance.now(), {
      world, env, agent: agentAnim,
      revealAll, showBelief, showSensor,
    });
    requestAnimationFrame(frame);
  });

  // ── Tick timer ────────────────────────────────────────────────────────
  function restartTickTimer(): void {
    if (tickTimerId !== null) window.clearInterval(tickTimerId);
    const periodMs = 1000 / Math.max(1, speedHz);
    tickTimerId = window.setInterval(() => { void tick(); }, periodMs);
  }

  async function tick(): Promise<void> {
    if (phase !== 'running') return;
    if (stepping) return;   // previous step still in flight
    if (env.done) {
      // Episode boundary: snapshot stats, generate a fresh layout, restart.
      episodes++;
      totalCollected += env.totalCollected;
      const g = makeClusteredWorld(GRID, (Math.random() * 1e9) | 0);
      world.loadFrom(g.cells, g.values);
      env.snapshotWorld();
      env.reset();
      activeAgent.startEpisode(env);
      agentAnim = makeAgentAnim(START_R, START_C);
      pushUpdate();
      return;
    }
    stepping = true;
    try {
      const res = await activeAgent.stepAsync(env);
      if (res.event === 'bump' || res.event === 'stone') agentAnim.bumpFlash = 1.0;
      lastQ = res.qValues;
      lastHNorm = res.hNorm;
      lastGate = res.gate;
      // Update the agent's heading so the renderer draws the sensor in the
      // right direction.
      const [dr, dc] = DIRECTIONS_STEP[res.action];
      if (dr !== 0 || dc !== 0) {
        agentAnim.targetHeading = Math.atan2(dr, dc);
      }
    } catch (e) {
      console.error('agent.stepAsync threw:', e);
      stopRun();
    } finally {
      stepping = false;
    }
    pushUpdate();
  }

  // The 8 directions in (dr, dc) form, for converting action → heading.
  // Mirrors environment.ts:DIRECTIONS.
  const DIRECTIONS_STEP: ReadonlyArray<readonly [number, number]> = [
    [0, 1], [0, -1], [1, 0], [-1, 0], [1, 1], [1, -1], [-1, 1], [-1, -1],
  ];

  function pushUpdate(): void {
    const u: PanelUpdate = {
      running: phase === 'running',
      steps: env.steps,
      collected: env.totalCollected,
      valueLeft: env.valueLeft,
      episodes,
      totalCollected,
      qValues: lastQ,
      hNorm: lastHNorm,
      lastRatio,
      comparing,
      gate: lastGate,
      gateEnabled,
      tabooEnabled,
      activeModel: activeVariant,
    };
    panel.update(u);
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────
  function startRun(): void {
    if (phase === 'running') return;
    phase = 'running';
    episodes = 0;
    totalCollected = 0;
    env.snapshotWorld();
    env.reset();
    activeAgent.startEpisode(env);
    agentAnim = makeAgentAnim(START_R, START_C);
    restartTickTimer();
    pushUpdate();
  }

  function stopRun(): void {
    if (phase !== 'running') return;
    phase = 'stopped';
    if (tickTimerId !== null) {
      window.clearInterval(tickTimerId);
      tickTimerId = null;
    }
    pushUpdate();
  }

  function resetRun(): void {
    stopRun();
    phase = 'idle';
    env.reset();
    agentAnim = makeAgentAnim(START_R, START_C);
    pushUpdate();
  }

  /** The comparator: 30 trained + 30 random episodes on matched layouts,
   *  starting from the current world. The trained ratio is the headline
   *  number for the demo. */
  async function runComparison(): Promise<void> {
    if (comparing) return;
    comparing = true;
    pushUpdate();
    try {
      // Snapshot the current world cells + values as the base layout.
      const baseCells = world.cells.slice();
      const baseValues = world.values.slice();

      let trainedTotal = 0;
      let randomTotal = 0;
      for (let i = 0; i < N_COMPARE_EPISODES; i++) {
        // Tiny jitter on each layout so they're not literally identical —
        // mirrors the prototype's eval_greedy which used fresh worlds per ep.
        // For the demo, we keep the user's layout but vary sensor noise via
        // the env's RNG.
        const wT = new World(GRID);
        wT.loadFrom(baseCells.slice(), baseValues.slice());
        const envT = new Env(wT, mulberry32(10_000 + i));
        const t = await runGreedyEpisode(activeAgent, envT);

        const wR = new World(GRID);
        wR.loadFrom(baseCells.slice(), baseValues.slice());
        const envR = new Env(wR, mulberry32(20_000 + i));
        const r = runRandomEpisode(envR);

        trainedTotal += t;
        randomTotal += r;
      }
      lastRatio = trainedTotal / Math.max(0.01, randomTotal);
      console.log(`comparison: trained=${trainedTotal / N_COMPARE_EPISODES}  `
                  + `random=${randomTotal / N_COMPARE_EPISODES}  ratio=${lastRatio}×`);
    } finally {
      comparing = false;
      pushUpdate();
    }
  }

  // ── Load model ────────────────────────────────────────────────────────
  try {
    // Pre-load BOTH models in parallel so the selector swap is instant.
    // Each model is ~57 KB / ~133 KB; parallel fetch keeps startup at the
    // slower of the two rather than their sum.
    await Promise.all([baseAgent.load(), memAgent.load()]);
    status.textContent = `Loaded. Grid ${GRID}×${GRID}, ${MAX_STEPS} steps/episode.`;
    status.classList.add('status-ok');
    setTimeout(() => status.remove(), 3000);
  } catch (e) {
    status.textContent = `Failed to load model: ${(e as Error).message}`;
    status.classList.add('status-fail');
    return;
  }

  // Initial render + first update.
  env.reset();
  pushUpdate();
}

// Resync helper: clone of Env.resyncValueLeft (declared on the env, but we
// add a top-level function for the paint path).
// (Actually Env already has resyncValueLeft; this declaration is unused.
// Kept as a marker — `world.paint` already updates valueLeft via goldValueTotal.)

main();
